import React, { useCallback, useMemo, useState } from "react";

import Card from "../../ui/Card";
import { useTheme } from "../../../contexts/ThemeContext";
import { hexToRgba } from "../../../utils/themes";

const STROKE = 13;
const SIZE = 156;
const COLORS_ALPHA = [1, 0.82, 0.66, 0.52, 0.4, 0.32, 0.26, 0.2];

/** Hover emphasizes one chart section; optional multi-select fades non-selected. */
function opacityForInteractive(legendKey, hoveredKey, selectedKeys) {
  if (hoveredKey && hoveredKey !== legendKey) return 0.28;
  if (hoveredKey === legendKey) return 1;
  if (selectedKeys.size > 0 && !selectedKeys.has(legendKey)) return 0.14;
  return 1;
}

function normalizeFractions(parts) {
  const raw = parts.map((p) => Math.max(0, Number(p) || 0));
  const sum = raw.reduce((a, b) => a + b, 0);
  if (sum <= 0) return raw.map(() => 0);
  return raw.map((x) => x / sum);
}

/** Single-ring radial: multi-brand slices by lead count per brand; single-brand shows average match progress. */
export default function AuditBrandFitRings({ radial, avgFitScore = null, title = "Best-fit matches" }) {
  const { theme } = useTheme();
  const [hoveredKey, setHoveredKey] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  const toggleLegendKey = useCallback((key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const chart = useMemo(() => {
    const r = radial || {};
    const brandCount = r.brand_count ?? 0;
    const totalLeads = r.total_leads ?? 0;
    const cold = r.cold_pitch_inbounds ?? 0;
    const slices = r.slices || [];

    if (!cold && totalLeads === 0) {
      return { segments: null, legend: [], mode: "empty" };
    }

    const cx = SIZE / 2;
    const cy = SIZE / 2;
    const radius = SIZE / 2 - STROKE / 2 - 6;
    const circum = 2 * Math.PI * radius;

    if (brandCount >= 2) {
      const nonzero = slices.filter((s) => (s.lead_count ?? 0) > 0);
      const sumLeads = nonzero.reduce((acc, s) => acc + (s.lead_count ?? 0), 0);
      if (sumLeads <= 0) {
        return { segments: null, legend: [], mode: "empty_multi" };
      }
      const fr = normalizeFractions(nonzero.map((s) => s.lead_count ?? 0));
      let rot = 0;
      const segments = nonzero.map((s, i) => {
        const len = fr[i] * circum;
        const dasharray = `${len} ${circum}`;
        const dashoffset = -rot;
        rot += len;
        return {
          dasharray,
          dashoffset,
          color: hexToRgba(theme.accent, COLORS_ALPHA[i % COLORS_ALPHA.length]),
          legendKey: `${s.brand_name}-${i}`,
        };
      });
      const legend = nonzero.map((s, i) => ({
        key: `${s.brand_name}-${i}`,
        label: s.brand_name,
        sub: `${s.lead_count ?? 0} leads`,
        leadCount: Number(s.lead_count ?? 0),
        dot: hexToRgba(theme.accent, COLORS_ALPHA[i % COLORS_ALPHA.length]),
      }));
      return { segments, legend, mode: "multi", circum, cx, cy, radius };
    }

    const averageFit = Math.min(100, Math.max(0, Number(avgFitScore) || 0));
    const progressLen = (averageFit / 100) * circum;
    const parts = [
      {
        dasharray: `${progressLen} ${circum}`,
        dashoffset: 0,
        color: theme.accent,
        legendKey: "avg",
      },
    ];

    const legend = [
      {
        key: "avg",
        label: "Average match score",
        sub: avgFitScore != null ? `${averageFit}%` : "Not enough matches",
        dot: theme.accent,
      },
    ];

    return {
      segments: parts,
      legend,
      mode: "single",
      averageFit,
      circum,
      cx,
      cy,
      radius,
    };
  }, [avgFitScore, radial, theme.accent]);

  const matchCount = Number(radial?.total_leads ?? 0) || 0;
  const centerMatches =
    chart.mode === "multi" && chart.legend?.length && selectedKeys.size > 0
      ? chart.legend
          .filter((l) => selectedKeys.has(l.key))
          .reduce((acc, l) => acc + (l.leadCount ?? 0), 0)
      : matchCount;

  const emptyCopy =
    chart.mode === "empty_multi"
      ? "No lead matches across brands in this view yet."
      : "No processed cold pitches in this view yet.";

  return (
    <Card className="p-4 md:p-5 h-full flex flex-col">
      <h3 className="font-display font-bold italic text-lg text-theme-on-background mb-1">{title}</h3>
      <p className="font-mono text-[10px] text-theme-dim mb-3 leading-snug">
        {chart.mode === "multi"
          ? "Matched leads split by the brand profile they fit best."
          : "Average fit score for matched leads, with match count in the center."}
      </p>
      {!chart.segments?.length ? (
        <p className="text-theme-muted text-sm mt-2">{emptyCopy}</p>
      ) : (
        <div className="flex flex-col items-center gap-4 flex-1 min-h-0">
          <div className="relative w-[156px] h-[156px] shrink-0">
            <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="shrink-0">
              {chart.mode === "single" ? (
                <circle
                  cx={chart.cx}
                  cy={chart.cy}
                  r={chart.radius}
                  fill="none"
                  stroke={hexToRgba(theme.border, 0.55)}
                  strokeWidth={STROKE}
                />
              ) : null}
              <g transform={`translate(${chart.cx},${chart.cy}) rotate(-90)`}>
                {chart.segments.map((seg, i) => {
                  const lk = seg.legendKey ?? `${i}`;
                  const o = opacityForInteractive(lk, hoveredKey, selectedKeys);
                  return (
                    <circle
                      key={`seg-${i}`}
                      cx={0}
                      cy={0}
                      r={chart.radius}
                      fill="none"
                      stroke={seg.color}
                      strokeOpacity={o}
                      strokeWidth={STROKE}
                      strokeLinecap="butt"
                      strokeDasharray={seg.dasharray}
                      strokeDashoffset={seg.dashoffset}
                      style={{ transition: "stroke-opacity 0.18s ease" }}
                    />
                  );
                })}
              </g>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-1">
              <div className="text-xl md:text-[1.4rem] font-bold text-theme-on-background tabular-nums leading-none text-center">
                {centerMatches}
              </div>
              <div className="mt-0.5 text-[11px] md:text-xs font-medium text-theme-text opacity-55 leading-none text-center">
                {centerMatches === 1 ? "match" : "matches"}
              </div>
            </div>
          </div>
          <ul className="w-full max-w-sm space-y-1.5 font-mono text-[11px] text-theme-text">
            {chart.legend.map((item) => {
              const sel = selectedKeys.has(item.key);
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    className={`flex items-start gap-2 min-w-0 w-full text-left rounded px-1 -mx-1 py-0.5 cursor-pointer transition-opacity duration-150 border border-transparent ${
                      sel ? "border-theme-secondary" : ""
                    }`}
                    style={{
                      opacity: opacityForInteractive(item.key, hoveredKey, selectedKeys),
                    }}
                    aria-pressed={sel}
                    onMouseEnter={() => setHoveredKey(item.key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    onClick={() => toggleLegendKey(item.key)}
                  >
                    <span
                      className="shrink-0 w-2 h-2 rounded-full mt-1"
                      style={{ background: item.dot }}
                    />
                    <span className="min-w-0 flex-1 pointer-events-none">
                      <span className="text-theme-on-background">{item.label}</span>
                      <span className="text-theme-text opacity-70"> · {item.sub}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}
